import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  Button,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Users,
  Search,
  Briefcase,
  MapPin,
  ChevronRight,
  BarChart3,
  FileText,
  FileSpreadsheet,
  X,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getMyTeam, getTeamMemberImage, getTeamMemberFeedbacks } from '../../services/projectManagerAPIs';
import PageHeader from '../headers/Header';
import marabesLogo from '../../assets/images/logos/Marabes.png';
import axopeiopleLogo from '../../assets/images/logos/Axopeople.png';

// ─── Helpers & export functions ───────────────────────────────────────────────
const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Could not load image for PDF:', url);
    return null;
  }
};

const MONTHS_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const fmtDate = (s) => {
  if (!s) return 'N/A';
  try {
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
  } catch { return 'N/A'; }
};

const exportTeamToPDF = (team, projectName) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;

  // --- Header logos ---
  try {
    doc.addImage(marabesLogo, 'PNG', margin, 8, 35, 12);
  } catch (e) {
    console.warn('Marabes logo load failed', e);
  }
  try {
    doc.addImage(axopeiopleLogo, 'PNG', pageWidth - margin - 35, 8, 35, 12);
  } catch (e) {
    console.warn('Axopeople logo load failed', e);
  }

  // --- Divider line ---
  doc.setDrawColor(59, 130, 246);
  doc.setLineWidth(0.5);
  doc.line(margin, 23, pageWidth - margin, 23);

  // --- Title ---
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(`Team — ${projectName}`, pageWidth / 2, 30, { align: 'center' });

  const rows = team.map((emp) => [
    `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.fullName || emp.name || 'N/A',
    (emp.positions || []).filter((p) => p?.positionName || p?.name).map((p) => p.positionName || p.name).join(', ') || emp.position || 'N/A',
    emp.department?.name || emp.departmentName || 'N/A',
    emp.office?.name || emp.officeName || 'N/A',
    getEmployeeProjectLabel(emp, [projectName]), // Use the PM's current project view
    emp.email || 'N/A',
  ]);
  autoTable(doc, {
    startY: 36,
    head: [['Full Name', 'Position', 'Department', 'Office', 'Project', 'Email']],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: [59, 130, 246], fontSize: 8, fontStyle: 'bold' },
    styles: { fontSize: 7.5, cellPadding: 2 },
    didDrawPage: (data) => {
      const pw = doc.internal.pageSize.width;
      const ph = doc.internal.pageSize.height;
      
      // Add logos to every page
      try {
        doc.addImage(marabesLogo, 'PNG', margin, 8, 35, 12);
      } catch (e) {
        // Silently fail
      }
      try {
        doc.addImage(axopeiopleLogo, 'PNG', pw - margin - 35, 8, 35, 12);
      } catch (e) {
        // Silently fail
      }
      
      // Add divider line
      doc.setDrawColor(59, 130, 246);
      doc.setLineWidth(0.5);
      doc.line(margin, 23, pw - margin, 23);
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150);
      const now = new Date();
      const ds = `${String(now.getDate()).padStart(2,'0')}-${String(now.getMonth()+1).padStart(2,'0')}-${now.getFullYear()}`;
      doc.text(`AxoPeople • ${ds} • Page ${doc.getCurrentPageInfo().pageNumber}`, pw / 2, ph - 8, { align: 'center' });
    },
  });
  const now = new Date();
  doc.save(`team_${projectName.replace(/\s+/g,'_')}_${now.getFullYear()}.pdf`);
};

const exportTeamToExcel = (team, projectName) => {
  const rows = team.map((emp) => ({
    'Full Name': `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.fullName || emp.name || 'N/A',
    'Position': (emp.positions || []).filter((p) => p?.positionName || p?.name).map((p) => p.positionName || p.name).join(', ') || emp.position || 'N/A',
    'Department': emp.department?.name || emp.departmentName || 'N/A',
    'Office': emp.office?.name || emp.officeName || 'N/A',
    'Project': getEmployeeProjectLabel(emp, [projectName]),
    'Email': emp.email || 'N/A',
    'Phone': emp.phone || 'N/A',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  if (rows.length > 0) {
    ws['!cols'] = Object.keys(rows[0]).map((k) => ({
      wch: Math.max(k.length, ...rows.map((r) => String(r[k] || '').length)),
    }));
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Team');
  const now = new Date();
  XLSX.writeFile(wb, `team_${projectName.replace(/\s+/g,'_')}_${now.getFullYear()}.xlsx`);
};

// ─── Export all team violations (PDF + Excel) ───────────────────────────────────
const exportAllTeamViolationsToPDF = async (team, projectName) => {
  if (!team || team.length === 0) return;
  
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;

  // --- Header logos on first page ---
  try {
    doc.addImage(marabesLogo, 'PNG', margin, 8, 35, 12);
  } catch (e) {
    console.warn('Marabes logo load failed', e);
  }
  try {
    doc.addImage(axopeiopleLogo, 'PNG', pageWidth - margin - 35, 8, 35, 12);
  } catch (e) {
    console.warn('Axopeople logo load failed', e);
  }

  // --- Divider line ---
  doc.setDrawColor(59, 130, 246);
  doc.setLineWidth(0.5);
  doc.line(margin, 23, pageWidth - margin, 23);

  // --- Title ---
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(`Team Violations Report — ${projectName}`, pageWidth / 2, 30, { align: 'center' });
  
  let yOffset = 36;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${fmtDate(new Date())}`, pageWidth / 2, yOffset, { align: 'center' });
  
  yOffset += 10;
  
  // Fetch violations for all team members
  try {
    for (const emp of team) {
      const empId = emp.employeeId || emp.id;
      const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown';
      
      try {
        const feedbacks = await getTeamMemberFeedbacks(empId);
        const violations = (feedbacks || []).filter(f => f.violationType);
        
        if (violations.length === 0) continue;
        
        // Employee name as section header
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        if (yOffset > doc.internal.pageSize.height - 40) {
          doc.addPage();
          yOffset = 20;
          
          // Add logos and divider to new pages
          try {
            doc.addImage(marabesLogo, 'PNG', margin, 8, 35, 12);
          } catch (e) { /* */ }
          try {
            doc.addImage(axopeiopleLogo, 'PNG', pageWidth - margin - 35, 8, 35, 12);
          } catch (e) { /* */ }
          doc.setDrawColor(59, 130, 246);
          doc.setLineWidth(0.5);
          doc.line(margin, 23, pageWidth - margin, 23);
          yOffset = 30;
        }
        doc.text(fullName, margin + 2, yOffset);
        yOffset += 6;
        
        // Violations table for this employee
        const rows = violations.map((v) => [
          fmtDate(v.incidentDate || v.date),
          v.violationType || 'General',
          v.description || '',
          v.managerReview || '',
          v.employeeReaction || '',
          v.employeeAgreement || '',
        ]);
        
        autoTable(doc, {
          startY: yOffset,
          head: [['Date', 'Type', 'Description', 'Manager Review', 'Employee Reaction', 'Agreement']],
          body: rows,
          theme: 'grid',
          headStyles: { fillColor: [33, 150, 243], fontSize: 6.5, fontStyle: 'bold', textColor: [255, 255, 255] },
          bodyStyles: { fontSize: 6, cellPadding: 1 },
          margin: { left: margin, right: margin },
          didDrawPage: (data) => {
            if (data.pageNumber > 1) {
              // Add logos to every new page
              try {
                doc.addImage(marabesLogo, 'PNG', margin, 8, 35, 12);
              } catch (e) { /* */ }
              try {
                doc.addImage(axopeiopleLogo, 'PNG', pageWidth - margin - 35, 8, 35, 12);
              } catch (e) { /* */ }
              
              doc.setDrawColor(59, 130, 246);
              doc.setLineWidth(0.5);
              doc.line(margin, 23, pageWidth - margin, 23);
            }
          }
        });
        
        yOffset = doc.lastAutoTable.finalY + 8;
      } catch (err) {
        console.warn(`Failed to fetch violations for ${empId}:`, err);
      }
    }
    
    // Footer on all pages
    const count = doc.getNumberOfPages();
    for (let i = 1; i <= count; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      const now = new Date();
      const ds = `${String(now.getDate()).padStart(2,'0')}-${String(now.getMonth()+1).padStart(2,'0')}-${now.getFullYear()}`;
      doc.text(`AxoPeople • ${ds} • Page ${i} of ${count}`, pageWidth / 2, doc.internal.pageSize.height - 8, { align: 'center' });
    }
    
    doc.save(`all_violations_${projectName.replace(/\s+/g,'_')}_${new Date().getFullYear()}.pdf`);
  } catch (err) {
    console.error('Error exporting violations to PDF:', err);
    alert('Failed to export violations. Please try again.');
  }
};

const exportAllTeamViolationsToExcel = async (team, projectName) => {
  if (!team || team.length === 0) return;
  
  try {
    const allRows = [];
    
    // Fetch violations for all team members
    for (const emp of team) {
      const empId = emp.employeeId || emp.id;
      const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown';
      
      try {
        const feedbacks = await getTeamMemberFeedbacks(empId);
        const violations = (feedbacks || []).filter(f => f.violationType);
        
        violations.forEach((v) => {
          allRows.push({
            'Employee Name': fullName,
            'Employee Email': emp.email || '—',
            'Department': emp.department?.name || emp.departmentName || '—',
            'Position': (emp.positions || []).filter(p => p?.positionName || p?.name).map(p => p.positionName || p.name).join(', ') || emp.position || '—',
            'Date': fmtDate(v.incidentDate || v.date),
            'Violation Type': v.violationType || 'General',
            'Description': v.description || '—',
            'Manager Review': v.managerReview || '—',
            'Employee Reaction': v.employeeReaction || '—',
            'Agreement': v.employeeAgreement || '—',
          });
        });
      } catch (err) {
        console.warn(`Failed to fetch violations for ${empId}:`, err);
      }
    }
    
    if (allRows.length === 0) {
      alert('No violations found for any team member.');
      return;
    }
    
    const ws = XLSX.utils.json_to_sheet(allRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'All Violations');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 18 }, // Employee Name
      { wch: 24 }, // Email
      { wch: 14 }, // Department
      { wch: 18 }, // Position
      { wch: 12 }, // Date
      { wch: 16 }, // Type
      { wch: 30 }, // Description
      { wch: 20 }, // Manager Review
      { wch: 20 }, // Reaction
      { wch: 16 }, // Agreement
    ];
    
    XLSX.writeFile(wb, `all_violations_${projectName.replace(/\s+/g,'_')}_${new Date().getFullYear()}.xlsx`);
  } catch (err) {
    console.error('Error exporting violations to Excel:', err);
    alert('Failed to export violations. Please try again.');
  }
};

// ─── Feedback Report Modal ─────────────────────────────────────────────────────
const FeedbackReportModal = ({ employee, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loadingFb, setLoadingFb] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState('All');

  const empId = employee?.employeeId || employee?.id;
  const fullName = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim();

  useEffect(() => {
    let active = true;
    setLoadingFb(true);
    getTeamMemberFeedbacks(empId)
      .then((data) => { if (active) setHistory(data || []); })
      .catch(() => { if (active) setHistory([]); })
      .finally(() => { if (active) setLoadingFb(false); });
    return () => { active = false; };
  }, [empId]);

  const filteredFb = useMemo(() => history.filter((n) => {
    const d = new Date(n.incidentDate || n.createdDate || n.date);
    if (isNaN(d)) return false;
    if (d.getFullYear() !== year) return false;
    if (month !== 'All' && d.getMonth() !== Number(month)) return false;
    return true;
  }), [history, year, month]);

  const grouped = useMemo(() => filteredFb.reduce((acc, n) => {
    const key = n.violationType || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(n);
    return acc;
  }, {}), [filteredFb]);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text(`Feedback Report — ${fullName}`, doc.internal.pageSize.width / 2, 18, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${year}${month !== 'All' ? ' — ' + MONTHS_NAMES[Number(month)] : ''}`, doc.internal.pageSize.width / 2, 26, { align: 'center' });
    const rows = filteredFb.map((n) => [
      fmtDate(n.incidentDate || n.date),
      n.violationType || 'General',
      n.description || '',
      n.managerReview || '',
      n.employeeReaction || '',
      n.employeeAgreement || '',
    ]);
    autoTable(doc, {
      startY: 32,
      head: [['Date', 'Type', 'Description', 'Manager Review', 'Employee Reaction', 'Agreement']],
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], fontSize: 7, fontStyle: 'bold' },
      styles: { fontSize: 6.5, cellPadding: 1.5 },
    });
    const count = doc.getNumberOfPages();
    for (let i = 1; i <= count; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`AxoPeople • Page ${i} of ${count}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 8, { align: 'center' });
    }
    doc.save(`feedback_${fullName.replace(/\s+/g,'_')}_${year}.pdf`);
  };

  const handleExportExcel = () => {
    const rows = filteredFb.map((n) => ({
      'Date': fmtDate(n.incidentDate || n.date),
      'Type': n.violationType || 'General',
      'Description': n.description || '',
      'Manager Review': n.managerReview || '',
      'Employee Reaction': n.employeeReaction || '',
      'Agreement': n.employeeAgreement || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feedback');
    XLSX.writeFile(wb, `feedback_${fullName.replace(/\s+/g,'_')}_${year}.xlsx`);
  };

  const thStyle = {
    padding: '12px 14px',
    textAlign: 'left',
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#1E293B',
    textTransform: 'uppercase',
    borderBottom: '2px solid #E2E8F0',
    backgroundColor: '#F9FAFB',
    whiteSpace: 'nowrap',
  };
  const tdStyle = {
    padding: '12px 14px',
    fontSize: '0.8rem',
    color: '#334155',
    verticalAlign: 'top',
    lineHeight: 1.6,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 16, width: '100%', maxWidth: 1100, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1E293B,#334155)', padding: '20px 24px', borderTopLeftRadius: 16, borderTopRightRadius: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: 'white', fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>Feedback Report</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', margin: '4px 0 0' }}>{fullName}{employee?.email ? ` — ${employee.email}` : ''}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 8, borderRadius: 8 }}>
            <X size={22} />
          </button>
        </div>
        {/* Toolbar */}
        <div style={{ padding: '12px 24px', backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: '0.875rem', backgroundColor: 'white' }}>
            {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: '0.875rem', backgroundColor: 'white' }}>
            <option value="All">All Months</option>
            {MONTHS_NAMES.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 600 }}>{filteredFb.length} record{filteredFb.length !== 1 ? 's' : ''}</span>
          <button onClick={handleExportPDF} disabled={filteredFb.length === 0} style={{ padding: '6px 14px', backgroundColor: filteredFb.length === 0 ? '#D1D5DB' : '#EF4444', color: 'white', border: 'none', borderRadius: 8, cursor: filteredFb.length === 0 ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={14} /> PDF
          </button>
          <button onClick={handleExportExcel} disabled={filteredFb.length === 0} style={{ padding: '6px 14px', backgroundColor: filteredFb.length === 0 ? '#D1D5DB' : '#22C55E', color: 'white', border: 'none', borderRadius: 8, cursor: filteredFb.length === 0 ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileSpreadsheet size={14} /> Excel
          </button>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {loadingFb ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading feedback history…</div>
          ) : filteredFb.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
              <BarChart3 size={40} style={{ opacity: 0.4, marginBottom: 12 }} />
              <p>No feedback records for this period.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Object.entries(grouped).map(([type, records]) => (
                <div key={type} style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 20px', backgroundColor: '#FEF3C7' }}>
                    <span style={{ fontWeight: 700, color: '#92400E', fontSize: '0.875rem' }}>
                      {type} <span style={{ fontWeight: 400, color: '#78350F' }}>({records.length})</span>
                    </span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, width: '12%' }}>Date</th>
                          <th style={{ ...thStyle, width: '28%' }}>Description</th>
                          <th style={{ ...thStyle, width: '22%' }}>Manager Review</th>
                          <th style={{ ...thStyle, width: '26%' }}>Employee Reaction</th>
                          <th style={{ ...thStyle, width: '12%' }}>Agreement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((n, i) => (
                          <tr key={i} style={{ borderBottom: i < records.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(n.incidentDate || n.date)}</td>
                            <td style={tdStyle}>{n.description || '—'}</td>
                            <td style={tdStyle}>{n.managerReview || '—'}</td>
                            <td style={tdStyle}>{n.employeeReaction || '—'}</td>
                            <td style={tdStyle}>{n.employeeAgreement || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Project helpers ─────────────────────────────────────────────────────────
/** Returns false for blank / N/A / not applicable sentinel values. */
const isValidProjectName = (name) => {
  if (!name || !name.trim()) return false;
  const lower = name.trim().toLowerCase();
  return lower !== 'n/a' && lower !== 'na' && lower !== 'not applicable' && lower !== '-';
};

/**
 * Parse both the main project and the '-'-separated secondProjectName from
 * localStorage into a deduplicated ordered array of all projects the PM manages.
 * Skips blank / N/A sentinel values.
 */
/**
 * Parse both the main project and the '-'-separated secondProjectName from
 * localStorage into a deduplicated ordered array of all projects the PM manages.
 * Skips blank / N/A sentinel values.
 */
const getPmManagedProjects = () => {
  const main = localStorage.getItem('userProjectName') || '';
  const second = localStorage.getItem('userSecondProjectName') || '';
  const projectsStr = localStorage.getItem('userProjects') || ''; // Support new storage if any
  
  const projects = [];
  if (isValidProjectName(main)) projects.push(main.trim());
  if (second.trim()) {
    second.split('-').forEach((p) => {
      const t = p.trim();
      if (isValidProjectName(t) && !projects.includes(t)) projects.push(t);
    });
  }
  if (projectsStr) {
    try {
      const parsed = JSON.parse(projectsStr);
      if (Array.isArray(parsed)) {
        parsed.forEach(p => {
          const name = typeof p === 'string' ? p : p.projectName;
          if (isValidProjectName(name) && !projects.includes(name)) projects.push(name);
        });
      }
    } catch(e) {}
  }
  return projects;
};

/**
 * Return the project name(s) that an employee shares with the PM.
 * Falls back to the employee's raw projectName if nothing matches.
 * Skips N/A sentinel values.
 */
const getEmployeeProjectLabel = (emp, pmProjects) => {
  const empProjects = (emp.projects || []).map(p => typeof p === 'string' ? p : (p.projectName || p.name)).filter(Boolean);
  
  if (!pmProjects || pmProjects.length === 0) {
    if (empProjects.length > 0) return empProjects.join(', ');
    return isValidProjectName(emp.projectName) ? emp.projectName : '—';
  }

  const matched = empProjects.filter(p => pmProjects.includes(p));
  
  // Fallback for non-migrated data if any
  if (matched.length === 0) {
    const ep = emp.projectName?.trim();
    if (ep && isValidProjectName(ep) && pmProjects.includes(ep)) matched.push(ep);
    if (emp.secondProjectName) {
      emp.secondProjectName.split('-').forEach((p) => {
        const t = p.trim();
        if (isValidProjectName(t) && pmProjects.includes(t) && !matched.includes(t)) matched.push(t);
      });
    }
  }

  return matched.length > 0 ? matched.join(', ') : (empProjects.length > 0 ? empProjects.join(', ') : (isValidProjectName(emp.projectName) ? emp.projectName : '—'));
};

// ─── Avatar initials ───────────────────────────────────────────────────────────
const getInitials = (firstName = '', lastName = '') =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

// ─── Inline row avatar with image loading ──────────────────────────────────────
const RowAvatar = ({ employee }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const first = employee?.firstName?.trim() || '';
  const last = employee?.lastName?.trim() || '';

  useEffect(() => {
    let active = true;
    if (employee?.image) {
      getTeamMemberImage(employee.employeeId || employee.id).then((url) => {
        if (active) setImgUrl(url);
      });
    }
    return () => { active = false; };
  }, [employee]);

  return (
    <Avatar
      src={imgUrl || undefined}
      sx={{
        width: 38, height: 38,
        bgcolor: '#90caf9', color: '#1565c0',
        fontSize: '0.8rem', fontWeight: 700,
        border: '2px solid #e3f2fd',
      }}
    >
      {!imgUrl && getInitials(first, last)}
    </Avatar>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────
const MyTeam = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';
  const pmProjects = getPmManagedProjects();
  // Display label: single project → its name, multiple → comma-separated list
  const projectName = pmProjects.length > 0 ? pmProjects.join(', ') : 'My Team';

  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(null); // null = all projects
  const [feedbackEmployee, setFeedbackEmployee] = useState(null);
  const [showFeedbackReport, setShowFeedbackReport] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getMyTeam()
      .then((data) => { if (active) setTeam(data || []); })
      .catch(() => { if (active) setError('Failed to load team members.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Distinct projects that actually appear in the loaded team
  const teamProjects = useMemo(() => {
    const seen = new Set();
    team.forEach((emp) => {
      // Use new projects array
      if (emp.projects && Array.isArray(emp.projects)) {
        emp.projects.forEach(p => {
          const name = typeof p === 'string' ? p : (p.projectName || p.name);
          if (isValidProjectName(name)) seen.add(name);
        });
      }
      
      // Fallback to old fields
      const ep = emp.projectName?.trim();
      if (isValidProjectName(ep)) seen.add(ep);
      if (emp.secondProjectName) {
        emp.secondProjectName.split('-').forEach((p) => {
          const t = p.trim();
          if (isValidProjectName(t)) seen.add(t);
        });
      }
    });
    return Array.from(seen).sort();
  }, [team]);

  const filtered = team.filter((emp) => {
    const name = `${emp.firstName || ''} ${emp.lastName || ''} ${emp.fullName || ''} ${emp.name || ''}`.toLowerCase();
    const dept = (emp.department?.name || emp.departmentName || '').toLowerCase();
    const pos = (
      (emp.positions || []).map((p) => p?.positionName || p?.name || '').join(' ') ||
      emp.position || ''
    ).toLowerCase();
    const q = search.toLowerCase();
    const matchesSearch = !q || name.includes(q) || dept.includes(q) || pos.includes(q);
    if (!matchesSearch) return false;
    if (!selectedProject) return true;

    // Check in new projects array
    const empProjects = (emp.projects || []).map(p => typeof p === 'string' ? p : (p.projectName || p.name)).filter(Boolean);
    if (empProjects.includes(selectedProject)) return true;

    // Fallback to old fields
    const ep = emp.projectName?.trim();
    if (ep && isValidProjectName(ep) && ep === selectedProject) return true;
    if (emp.secondProjectName) {
      return emp.secondProjectName.split('-').some((p) => {
        const t = p.trim();
        return isValidProjectName(t) && t === selectedProject;
      });
    }
    return false;
  });

  // Style helpers
  const thSx = {
    py: 1.5, px: 2,
    fontSize: '0.7rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    color: isDark ? '#94a3b8' : '#697586',
    borderBottom: '2px solid',
    borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#e3e8ef',
    bgcolor: isDark ? '#0f172a' : '#f8fafc',
    whiteSpace: 'nowrap',
  };

  const tdSx = {
    py: 1.25, px: 2,
    fontSize: '0.8rem',
    borderBottom: '1px solid',
    borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#eef2f6',
    color: isDark ? '#e2e8f0' : '#364152',
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title={`Team — ${projectName}`}
        subtitle="View your team members and manage their feedback"
      />

      {/* Toolbar: Search + Export */}
      <Box
        sx={{
          mb: 2.5,
          display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center',
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#0f172a' : '#ffffff',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#e3e8ef',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)',
        }}
      >
        <TextField
          size="small"
          placeholder="Search by name, position or department…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color="#94a3b8" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 280 } }}
        />

        {/* Project filter dropdown */}
        {teamProjects.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel
              sx={{
                fontSize: '0.8rem',
                color: isDark ? '#94a3b8' : '#697586',
              }}
            >
              Project
            </InputLabel>
            <Select
              value={selectedProject || ''}
              label="Project"
              onChange={(e) => setSelectedProject(e.target.value || null)}
              sx={{
                fontSize: '0.8rem',
                bgcolor: isDark ? '#0f172a' : '#fff',
                color: isDark ? '#e2e8f0' : '#364152',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2196f3',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2196f3',
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All</MenuItem>
              {teamProjects.map((proj) => (
                <MenuItem key={proj} value={proj} sx={{ fontSize: '0.8rem' }}>{proj}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#697586', fontWeight: 600, ml: 1 }}>
          {filtered.length} member{filtered.length !== 1 ? 's' : ''}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
          {/* Team Export */}
          <Tooltip title="Export team member list">
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileText size={14} />}
              onClick={() => exportTeamToPDF(filtered, projectName)}
              disabled={filtered.length === 0}
              sx={{ borderColor: '#ef4444', color: '#ef4444', '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' }, textTransform: 'none', fontSize: '0.8rem' }}
            >
              PDF
            </Button>
          </Tooltip>
          <Tooltip title="Export team member list">
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileSpreadsheet size={14} />}
              onClick={() => exportTeamToExcel(filtered, projectName)}
              disabled={filtered.length === 0}
              sx={{ borderColor: '#22c55e', color: '#22c55e', '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' }, textTransform: 'none', fontSize: '0.8rem' }}
            >
              Excel
            </Button>
          </Tooltip>

          {/* Violations Export */}
          <Box sx={{ borderLeft: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e3e8ef', pl: 1.5, ml: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Export all violations from team">
              <Button
                variant="outlined"
                size="small"
                startIcon={<BarChart3 size={14} />}
                onClick={() => exportAllTeamViolationsToPDF(team, projectName)}
                disabled={team.length === 0}
                sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' }, textTransform: 'none', fontSize: '0.8rem' }}
              >
                Violations PDF
              </Button>
            </Tooltip>
            <Tooltip title="Export all violations from team">
              <Button
                variant="outlined"
                size="small"
                startIcon={<BarChart3 size={14} />}
                onClick={() => exportAllTeamViolationsToExcel(team, projectName)}
                disabled={team.length === 0}
                sx={{ borderColor: '#06b6d4', color: '#06b6d4', '&:hover': { borderColor: '#0891b2', bgcolor: '#ecfdf5' }, textTransform: 'none', fontSize: '0.8rem' }}
              >
                Violations Excel
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center', py: 10,
            border: '1px dashed', borderColor: 'divider', borderRadius: 3,
            bgcolor: isDark ? '#0f172a' : '#fafbfc',
          }}
        >
          <Users size={44} color="#94a3b8" style={{ marginBottom: 12 }} />
          <Typography color="text.secondary">
            {search ? 'No team members match your search.' : 'No team members found.'}
          </Typography>
        </Box>
      ) : (
        /* ─── Data Table ─── */
        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#e3e8ef',
            bgcolor: isDark ? '#1e293b' : '#ffffff',
            boxShadow: '0 2px 14px 0 rgb(32 40 45 / 8%)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr>
                  <Box component="th" sx={{ ...thSx, width: 56, px: 1.5 }} />
                  <Box component="th" sx={{ ...thSx, textAlign: 'left' }}>Employee</Box>
                  <Box component="th" sx={{ ...thSx, textAlign: 'left' }}>Position</Box>
                  <Box component="th" sx={{ ...thSx, textAlign: 'left' }}>Department</Box>
                  <Box component="th" sx={{ ...thSx, textAlign: 'left' }}>Office</Box>
                  <Box component="th" sx={{ ...thSx, textAlign: 'left' }}>Project</Box>
                  <Box component="th" sx={{ ...thSx, width: 90, textAlign: 'center' }}>Actions</Box>
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp) => {
                  const first = emp.firstName?.trim() || '';
                  const last = emp.lastName?.trim() || '';
                  const fullName = `${first} ${last}`.trim() || emp.fullName || emp.name || 'Unknown';
                  const positions = (emp.positions || [])
                    .filter((p) => p?.positionName || p?.name)
                    .map((p) => p.positionName || p.name)
                    .join(', ') || emp.position || '—';
                  const dept = emp.department?.name || emp.departmentName || '—';
                  const office = emp.office?.name || emp.officeName || '—';
                  const project = getEmployeeProjectLabel(emp, pmProjects);
                  const empId = emp.employeeId || emp.id;

                  return (
                    <Box
                      component="tr"
                      key={empId}
                      onClick={() => navigate(`/team-profile/${empId}`)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(33,150,243,0.06)' : '#f0f7ff',
                        },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      {/* Avatar */}
                      <Box component="td" sx={{ ...tdSx, px: 1.5, width: 56 }}>
                        <RowAvatar employee={emp} />
                      </Box>

                      {/* Name */}
                      <Box component="td" sx={tdSx}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: isDark ? '#f1f5f9' : '#1e293b', lineHeight: 1.3 }}>
                          {fullName}
                        </Typography>
                        {emp.email && (
                          <Typography sx={{ fontSize: '0.7rem', color: isDark ? '#64748b' : '#94a3b8', mt: 0.2 }}>
                            {emp.email}
                          </Typography>
                        )}
                      </Box>

                      {/* Position */}
                      <Box component="td" sx={tdSx}>
                        <Chip
                          label={positions}
                          size="small"
                          icon={<Briefcase size={10} />}
                          sx={{
                            maxWidth: 200,
                            fontSize: '0.7rem', height: 22, fontWeight: 500,
                            bgcolor: isDark ? 'rgba(33,150,243,0.1)' : '#e3f2fd',
                            color: isDark ? '#90caf9' : '#1565c0',
                            border: '1px solid',
                            borderColor: isDark ? 'rgba(33,150,243,0.2)' : '#bbdefb',
                            '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                            '& .MuiChip-label': { px: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                          }}
                        />
                      </Box>

                      {/* Department */}
                      <Box component="td" sx={tdSx}>
                        <Chip
                          label={dept}
                          size="small"
                          sx={{
                            fontSize: '0.7rem', height: 22, fontWeight: 500,
                            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#eef2f6',
                            color: isDark ? '#94a3b8' : '#697586',
                            '& .MuiChip-label': { px: '8px' },
                          }}
                        />
                      </Box>

                      {/* Office */}
                      <Box component="td" sx={tdSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapPin size={12} color={isDark ? '#64748b' : '#94a3b8'} />
                          <Typography sx={{ fontSize: '0.8rem' }}>{office}</Typography>
                        </Box>
                      </Box>

                      {/* Project */}
                      <Box component="td" sx={tdSx}>
                        <Typography sx={{ fontSize: '0.8rem' }}>{project}</Typography>
                      </Box>

                      {/* Actions */}
                      <Box component="td" sx={{ ...tdSx, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <Tooltip title="View Profile" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/team-profile/${empId}`); }}
                            sx={{
                              color: isDark ? '#94a3b8' : '#697586',
                              '&:hover': { color: '#2196f3', bgcolor: isDark ? 'rgba(33,150,243,0.1)' : '#e3f2fd' },
                            }}
                          >
                            <ChevronRight size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Feedback Report" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setFeedbackEmployee(emp); setShowFeedbackReport(true); }}
                            sx={{
                              color: isDark ? '#64748b' : '#94a3b8',
                              '&:hover': { color: '#2196f3', bgcolor: isDark ? 'rgba(33,150,243,0.1)' : '#e3f2fd' },
                            }}
                          >
                            <BarChart3 size={15} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Feedback Report Modal */}
      {showFeedbackReport && feedbackEmployee && (
        <FeedbackReportModal
          employee={feedbackEmployee}
          onClose={() => { setShowFeedbackReport(false); setFeedbackEmployee(null); }}
        />
      )}
    </Box>
  );
};

export default MyTeam;
